import React, { useEffect, useState } from "react";
import { useDocumentTitle } from "$utils/document-title";
import { format } from "date-fns";
import { SLASH_DATE_TIME_FORMAT } from "$utils/date-helpers";
import canaryApi from "./api";
import api from "$services/api";

const compareValues = (sourceVal, apiVal) => {
    if (sourceVal === null || sourceVal === undefined) return { status: "skip" };
    if (apiVal === null || apiVal === undefined) return { status: "fail", note: "API returned null" };

    if (typeof sourceVal === "number") {
        const diff = Math.abs(sourceVal - apiVal);
        const pct = sourceVal !== 0 ? diff / Math.abs(sourceVal) : diff;
        return pct < 0.001
            ? { status: "pass" }
            : { status: "fail", note: `source=${sourceVal.toLocaleString()}, api=${apiVal.toLocaleString()}` };
    }

    if (typeof sourceVal === "string") {
        return sourceVal.toLowerCase() === String(apiVal).toLowerCase()
            ? { status: "pass" }
            : { status: "fail", note: `source="${sourceVal}", api="${apiVal}"` };
    }

    return { status: "pass" };
};

const diffSnapshot = (sourceJson, apiData) => {
    let source;
    try { source = JSON.parse(sourceJson); } catch { return []; }

    return [
        { field: "Name",               result: compareValues(source.name,               apiData?.name) },
        { field: "Sector",             result: compareValues(source.sector,             (apiData?.industries || []).map((i) => i.text).join("|")) },
        { field: "Department",         result: compareValues(source.department,         (apiData?.practices  || []).map((p) => p.text).join("|")) },
        { field: "Office",             result: compareValues(source.office,             apiData?.primaryOfficeName) },
        { field: "T12 Revenue",        result: compareValues(source.t12Revenue,         apiData?.twelveMonthRevenue) },
        { field: "CRM Count",          result: compareValues(source.crmCount,           (apiData?.crms || []).length) },
        { field: "Req. Independence",  result: compareValues(source.requiresIndependence, apiData?.requiresIndependence ? 1 : 0) },
    ].filter((f) => f.result.status !== "skip");
};

const FieldResult = ({ field, result }) => (
    <div style={{ display: "flex", gap: "12px", alignItems: "baseline", padding: "3px 0" }}>
        <span style={{ minWidth: "160px", fontSize: "0.875rem" }}>{field}</span>
        <span style={{ fontWeight: "bold", color: result.status === "pass" ? "green" : "red" }}>
            {result.status.toUpperCase()}
        </span>
        {result.note && <span style={{ color: "#666", fontSize: "0.8rem" }}>{result.note}</span>}
    </div>
);

const ClientCard = ({ snapshot, apiData, loading }) => {
    const diffs = !loading && apiData ? diffSnapshot(snapshot.sourceJson, apiData) : [];
    const failCount = diffs.filter((d) => d.result.status === "fail").length;

    return (
        <div style={{ border: `1px solid ${failCount > 0 ? "#e53e3e" : "#48bb78"}`, borderRadius: "6px", padding: "16px", marginBottom: "12px" }}>
            <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "8px" }}>
                <h4 style={{ margin: 0 }}>{snapshot.clientGroupName}</h4>
                <span className="txt -muted" style={{ fontSize: "0.75rem" }}>{snapshot.isFixed ? "Tracked" : "Random"}</span>
                {loading ? (
                    <span className="txt -muted">Loading...</span>
                ) : (
                    <span style={{ color: failCount > 0 ? "#e53e3e" : "#48bb78", fontWeight: "bold" }}>
                        {failCount > 0 ? `${failCount} issue${failCount > 1 ? "s" : ""}` : "All pass"}
                    </span>
                )}
            </div>
            {!loading && diffs.map((d) => <FieldResult key={d.field} field={d.field} result={d.result} />)}
        </div>
    );
};

const CanaryValidationPage = () => {
    useDocumentTitle("Canary Validation");

    const [snapshots, setSnapshots] = useState([]);
    const [apiResults, setApiResults] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingIds, setLoadingIds] = useState(new Set());

    useEffect(() => {
        const controller = new AbortController();
        canaryApi.getLatestSnapshots(controller.signal).then(async (snaps) => {
            if (!snaps) { setLoading(false); return; }
            setSnapshots(snaps);
            setLoading(false);

            const clientSnaps = snaps.filter((s) => !s.isFirmSummary && s.clientGroupId);
            setLoadingIds(new Set(clientSnaps.map((s) => s.id)));

            await Promise.all(clientSnaps.map(async (snap) => {
                try {
                    const response = await api.get(`/api/client-groups?client=${snap.clientGroupId}&page=0&limit=1`);
                    const data = response?.data?.[0] || null;
                    setApiResults((prev) => ({ ...prev, [snap.id]: data }));
                } catch {
                    setApiResults((prev) => ({ ...prev, [snap.id]: null }));
                } finally {
                    setLoadingIds((prev) => { const next = new Set(prev); next.delete(snap.id); return next; });
                }
            }));
        });
        return () => controller.abort();
    }, []);

    if (loading) return <div>Loading canary snapshots...</div>;

    const firmSummary = snapshots.find((s) => s.isFirmSummary);
    const fixedClients = snapshots.filter((s) => !s.isFirmSummary && s.isFixed);
    const randomClients = snapshots.filter((s) => !s.isFirmSummary && !s.isFixed);

    if (snapshots.length === 0) return (
        <div>
            <h1>Canary Validation</h1>
            <p>No snapshots found. Run the ETL after configuring canary clients.</p>
        </div>
    );

    let firmData = null;
    try { if (firmSummary) firmData = JSON.parse(firmSummary.sourceJson); } catch { /* empty */ }

    const randomPassCount = randomClients.filter((s) => {
        const diffs = apiResults[s.id] ? diffSnapshot(s.sourceJson, apiResults[s.id]) : [];
        return diffs.length > 0 && diffs.every((d) => d.result.status === "pass");
    }).length;

    return (
        <div>
            <h1>Canary Validation</h1>
            {firmSummary && (
                <p className="txt -muted">
                    ETL run: {format(new Date(firmSummary.runDate), SLASH_DATE_TIME_FORMAT)}
                </p>
            )}

            {firmData && (
                <section style={{ marginBottom: "32px" }}>
                    <h2>Firm Summary</h2>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                        {[
                            ["Active Client Groups", firmData.activeClientGroups],
                            ["Active Matters", firmData.activeMatters],
                            ["Firm T12 Revenue", `$${(firmData.totalFirmT12Revenue || 0).toLocaleString()}`],
                            ["Current Period Revenue", `$${(firmData.currentPeriodRevenue || 0).toLocaleString()}`],
                            ["Clients with Sector", firmData.clientGroupsWithSector],
                        ].map(([label, val]) => (
                            <div key={label} style={{ border: "1px solid #e2e8f0", borderRadius: "6px", padding: "16px", minWidth: "160px" }}>
                                <h5 style={{ margin: "0 0 4px" }}>{label}</h5>
                                <p style={{ margin: 0, fontWeight: "bold" }}>{val?.toLocaleString?.() ?? val}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {fixedClients.length > 0 && (
                <section style={{ marginBottom: "32px" }}>
                    <h2>Tracked Clients</h2>
                    {fixedClients.map((s) => (
                        <ClientCard key={s.id} snapshot={s} apiData={apiResults[s.id]} loading={loadingIds.has(s.id)} />
                    ))}
                </section>
            )}

            {randomClients.length > 0 && (
                <section>
                    <h2>Random Spot Checks</h2>
                    <p className="txt -muted">
                        {randomPassCount} / {randomClients.length} passing
                        {randomClients.length > 0 && ` (${Math.round((randomPassCount / randomClients.length) * 100)}%)`}
                    </p>
                    {randomClients.map((s) => (
                        <ClientCard key={s.id} snapshot={s} apiData={apiResults[s.id]} loading={loadingIds.has(s.id)} />
                    ))}
                </section>
            )}
        </div>
    );
};

export default CanaryValidationPage;
